import React from "react";
import { LocalStorageKeys } from "../constants";
import { ThemeMode, ResolvedThemeMode } from "../theme";

export interface ThemeModeContextValue {
  mode: ThemeMode;
  resolvedMode: ResolvedThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

export const ThemeModeContext = React.createContext<
  ThemeModeContextValue | undefined
>(undefined);

export function useThemeMode(): ThemeModeContextValue {
  const context = React.useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
}

function getSystemPreference(): ResolvedThemeMode {
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

function getStoredThemeMode(): ThemeMode {
  if (typeof window === "undefined") {
    return "light";
  }

  const stored = localStorage.getItem(LocalStorageKeys.THEME_MODE);
  if (stored === "dark" || stored === "light" || stored === "system") {
    return stored;
  }

  return "light";
}

function resolveMode(mode: ThemeMode): ResolvedThemeMode {
  if (mode === "system") {
    return getSystemPreference();
  }
  return mode;
}

export function useThemeModeState(): ThemeModeContextValue {
  const [mode, setModeState] = React.useState<ThemeMode>(getStoredThemeMode);
  const [resolvedMode, setResolvedMode] = React.useState<ResolvedThemeMode>(
    () => resolveMode(getStoredThemeMode()),
  );

  const setMode = React.useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(LocalStorageKeys.THEME_MODE, newMode);
    setResolvedMode(resolveMode(newMode));
  }, []);

  const toggleMode = React.useCallback(() => {
    const next = mode === "light" ? "dark" : "light";
    setMode(next);
  }, [mode, setMode]);

  // Listen for system preference changes — only affects resolved mode when mode is "system"
  React.useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mediaQuery) return;

    const handleChange = (e: MediaQueryListEvent) => {
      if (mode === "system") {
        setResolvedMode(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [mode]);

  return { mode, resolvedMode, setMode, toggleMode };
}
